import {
    CircularProgress,
    Table as MuiTable,
    TableCell, TableFooter as MuiTableFooter, TablePagination, TableRow } from "@mui/material";
import { TableFooter as DefaultTableFooter } from "mui-datatables";
import { createContext, useContext } from "react";

/**
 * True while the caller is still loading the exact total of the list. The table then
 * shows a loading animation in place of the total instead of a provisional number
 * (the rows loaded so far), which reads like a real count ("1-10 of 10").
 */
export const CountPendingContext = createContext(false);

export interface PendingCountFooterProps {
    options: any;
    rowCount: number;
    page: number;
    rowsPerPage: number;
    changeRowsPerPage: (rows: number) => void;
    changePage: (page: number) => void;
}

/**
 * Whether the pending-count footer applies. The provisional row count is the rows up to
 * the end of the current page. A page that is not full is the last page, so its
 * provisional count is already the real total and the normal footer shows it.
 */
export const isCountPendingForPage = (
    countPending: boolean,
    rowCount: number,
    page: number,
    rowsPerPage: number
): boolean => countPending && rowCount >= (page + 1) * rowsPerPage;

/**
 * mui-datatables TableFooter replacement. While the total is loading on a full page it
 * renders the same pagination with "1-10 of" followed by a small spinner and keeps Next
 * enabled (MUI's count=-1 "unknown total" mode); otherwise it renders the default footer.
 */
export default function PendingCountTableFooter(props: PendingCountFooterProps) {
    const countPending = useContext(CountPendingContext);
    const { options, rowCount, page, rowsPerPage, changeRowsPerPage, changePage } = props;
    if (!isCountPendingForPage(countPending, rowCount, page, rowsPerPage)) {
        return <DefaultTableFooter {...(props as any)} />;
    }
    if (options?.pagination === false) {
        return null;
    }
    const labels = options?.textLabels?.pagination ?? {};
    return (
        <MuiTable>
            <MuiTableFooter>
                <TableRow>
                    <TableCell colSpan={1000} style={{ padding: "0px 24px" }}>
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <TablePagination
                                component="div"
                                count={-1}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                rowsPerPageOptions={options?.rowsPerPageOptions}
                                labelRowsPerPage={labels.rowsPerPage}
                                labelDisplayedRows={({ from, to }) => (
                                    <span
                                        className="recordTable__countPending"
                                        data-testid="pagination-count-pending"
                                    >
                                        {`${from}-${to} ${labels.displayRows ?? "of"} `}
                                        <CircularProgress
                                            size={12}
                                            thickness={5}
                                            color="inherit"
                                            aria-label="Loading total"
                                        />
                                    </span>
                                )}
                                backIconButtonProps={{
                                    id: "pagination-back",
                                    "aria-label": labels.previous,
                                    title: labels.previous || "",
                                }}
                                nextIconButtonProps={{
                                    id: "pagination-next",
                                    "aria-label": labels.next,
                                    title: labels.next || "",
                                }}
                                onPageChange={(_event, newPage) => changePage(newPage)}
                                onRowsPerPageChange={(event) =>
                                    changeRowsPerPage(Number(event.target.value))
                                }
                            />
                        </div>
                    </TableCell>
                </TableRow>
            </MuiTableFooter>
        </MuiTable>
    );
}
